import React, {useState, useEffect, useRef} from 'react';
import {Html5Qrcode, Html5QrcodeSupportedFormats} from 'html5-qrcode';
import {useDatabase} from '../context/DatabaseContext';
import {useLocation} from 'react-router-dom';
import {useToast} from '../context/ToastContext';
import {useUserNickname} from '../hooks/useUserNickname';
import {colors} from '../theme/colors';

const ScanScreen = () => {
  const {addFood, addEntry, getAllFoods} = useDatabase();
  const {success, error: showError} = useToast();
  const userNickname = useUserNickname();
  const location = useLocation();
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  
  const [scanning, setScanning] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [cameraId, setCameraId] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [isScanningPaused, setIsScanningPaused] = useState(false);
  const [lastAddedProduct, setLastAddedProduct] = useState(null);
  const lastScannedBarcodeRef = useRef(null);
  const lastScanTimeRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const isAddingProductRef = useRef(false); // Prevent multiple simultaneous additions
  // Extra guards against duplicate callbacks / restarts
  const inFlightBarcodeRef = useRef(null);
  const processedBarcodesRef = useRef(new Map()); // barcode -> { t: number, status: 'success'|'not_found'|'fetch_error' }
  const BARCODE_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

  // Helper function to detect iOS devices
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  useEffect(() => {
    // Only run when entering the scan screen
    if (location.pathname === '/scan') {
      // Clear any previous errors
      setCameraError(null);
      setError(null);
      
      // Always reset state when entering scan screen
      lastScannedBarcodeRef.current = null;
      lastScanTimeRef.current = null;
      isAddingProductRef.current = false;
      
      // DO NOT automatically start scanning - wait for user to click "Start Scan" button
      // This prevents camera permission errors on page load
      
      return () => {
        // Cleanup on unmount or path change
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
          scanTimeoutRef.current = null;
        }
        // Reset flags
        isAddingProductRef.current = false;
        lastScannedBarcodeRef.current = null;
        lastScanTimeRef.current = null;
        stopScanning();
      };
    }
  }, [location.pathname]);

  // Get available cameras
  const getCameras = async (retries = 3) => {
    // iPhone Safari often has issues with camera enumeration
    // Skip enumeration on iOS and use facingMode directly (more reliable)
    if (isIOS()) {
      console.log('[ScanScreen] iOS detected, skipping camera enumeration - will use facingMode');
      return null; // Will use facingMode fallback
    }
    
    // On some Android Chrome versions, enumerateDevices may temporarily return []
    // right after stopping a stream. Retry a few times before giving up.
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length > 0) {
          setAvailableCameras(devices);
          // Prefer back camera (environment), fallback to first available
          const backCamera = devices.find(d => (d.label || '').toLowerCase().includes('back') || (d.label || '').toLowerCase().includes('rear'));
          return backCamera || devices[0];
        }
      } catch (err) {
        console.error('[ScanScreen] Error getting cameras:', err);
      }

      // Wait a bit and retry
      await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
    }
    return null;
  };

  const startScanning = async () => {
    console.log('[ScanScreen] startScanning called');
    // #region agent log (H1/H2) startScanning entry
    fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H2',location:'src/screens/ScanScreen.js:startScanning:entry',message:'startScanning called',data:{scanning,hasRef:!!html5QrCodeRef.current,isScanningPaused,loading,hasTimeout:!!scanTimeoutRef.current,protocol:window.location.protocol,host:window.location.host,container:!!document.getElementById('scanner-container'),videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState,enabled:x.enabled,muted:x.muted}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
    // #endregion agent log
    try {
      // Clear any pending timeouts
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      
      // Clean up any existing scanner instance before creating a new one
      if (html5QrCodeRef.current) {
        // #region agent log (H1/H2) startScanning cleanup existing ref
        fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H2',location:'src/screens/ScanScreen.js:startScanning:cleanupExisting',message:'Cleaning existing html5QrCodeRef before start',data:{scanning,hasRef:!!html5QrCodeRef.current,hasStop:typeof html5QrCodeRef.current?.stop==='function',hasClear:typeof html5QrCodeRef.current?.clear==='function'},timestamp:Date.now()})}).catch(()=>{});
        // #endregion agent log
        try {
          // Always try to stop first (safe even if already stopped)
          await html5QrCodeRef.current.stop().catch(() => {});
          // Clear the scanner from DOM
          await html5QrCodeRef.current.clear().catch(() => {});
        } catch (err) {
          console.log('[ScanScreen] Error cleaning up existing scanner:', err);
        }
        html5QrCodeRef.current = null;
      }
      
      // Reset all state and flags
      setError(null);
      setCameraError(null); // Clear error so overlay disappears
      setProduct(null);
      setLastAddedProduct(null);
      setIsScanningPaused(false);
      isAddingProductRef.current = false;
      // IMPORTANT: do NOT reset lastScannedBarcodeRef/lastScanTimeRef here.
      // Clearing them makes the same visible barcode look "new" after restarts and can cause loops.
      // Only explicit user actions (e.g. a dedicated "scan again same barcode") should clear these.
      inFlightBarcodeRef.current = null;
      
      // Find the scanner container DOM element (always rendered now)
      const scannerContainer = document.getElementById('scanner-container');
      if (!scannerContainer) {
        console.error('[ScanScreen] Scanner container not found in DOM');
        // Wait a moment and try again (in case React hasn't rendered yet)
        await new Promise(resolve => setTimeout(resolve, 200));
        const retryContainer = document.getElementById('scanner-container');
        if (!retryContainer) {
          throw new Error('Scanner container element not found. Please refresh the page.');
        }
        // #region agent log (H6/H7) startScanning clearing DOM (retry container)
        fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H6/H7',location:'src/screens/ScanScreen.js:startScanning:clearDom:retry',message:'Clearing scanner-container innerHTML (retry path)',data:{hasVideo:!!document.querySelector('#scanner-container video'),videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject},timestamp:Date.now()})}).catch(()=>{});
        // #endregion agent log
        retryContainer.innerHTML = '';
      } else {
        // #region agent log (H6/H7) startScanning clearing DOM
        fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H6/H7',location:'src/screens/ScanScreen.js:startScanning:clearDom',message:'Clearing scanner-container innerHTML',data:{hasVideo:!!document.querySelector('#scanner-container video'),videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject},timestamp:Date.now()})}).catch(()=>{});
        // #endregion agent log
        scannerContainer.innerHTML = '';
      }
      
      console.log('[ScanScreen] Getting cameras...');
      // Get available cameras first
      const selectedCamera = await getCameras();
      if (!selectedCamera) {
        // IMPORTANT: Don't hard-fail here. Some phones return empty cameras list after stopping.
        // We can still start using facingMode and let the browser pick the correct camera.
        console.warn('[ScanScreen] No cameras returned from getCameras(); falling back to facingMode');
        setAvailableCameras([]);
        setCameraId(null);
      } else {
        console.log('[ScanScreen] Camera found:', selectedCamera.label);
      }

      // Small delay to ensure DOM is ready after cleanup
      await new Promise(resolve => setTimeout(resolve, 200));

      const html5QrCode = new Html5Qrcode("scanner-container");
      html5QrCodeRef.current = html5QrCode;

      // Improved configuration for better barcode scanning
      // iPhone-specific optimizations: lower FPS, simpler constraints
      const config = {
        fps: isIOS() ? 5 : 10, // Lower FPS for iPhone (better stability)
        qrbox: function(viewfinderWidth, viewfinderHeight) {
          // Dynamic scanning box - MUST ALWAYS satisfy library requirement: >= 50px
          // Some Android browsers briefly report 0x0 viewfinder; handle that safely.
          const w = Number(viewfinderWidth) || 0;
          const h = Number(viewfinderHeight) || 0;
          const minEdgeSize = Math.min(w, h);

          const MIN_QRBOX_SIZE = 50;
          // If we don't have real dimensions yet, return the minimum safe size.
          if (!minEdgeSize || minEdgeSize < (MIN_QRBOX_SIZE + 10)) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[ScanScreen] QRBox using fallback min size due to small/unknown viewfinder:', w, 'x', h);
            }
            return { width: MIN_QRBOX_SIZE, height: MIN_QRBOX_SIZE };
          }

          const minEdgePercentage = 0.7;
          let qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);

          // Clamp to [MIN_QRBOX_SIZE, minEdgeSize - 10]
          const maxAllowed = Math.max(MIN_QRBOX_SIZE, minEdgeSize - 10);
          qrboxSize = Math.max(MIN_QRBOX_SIZE, Math.min(qrboxSize, maxAllowed));

          if (process.env.NODE_ENV === 'development') {
            console.log('[ScanScreen] QRBox size calculated:', qrboxSize, 'from viewfinder:', w, 'x', h);
          }

          return { width: qrboxSize, height: qrboxSize };
        },
        aspectRatio: isIOS() ? undefined : 1.0, // Don't force aspect ratio on iOS (Safari handles it better)
        disableFlip: false, // Allow rotation
        videoConstraints: isIOS() ? {
          // Simpler constraints for iPhone Safari (more reliable)
          facingMode: "environment",
          // Don't specify width/height on iOS - let Safari choose optimal resolution
        } : {
          facingMode: "environment", // Prefer back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        // Support multiple barcode formats
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.QR_CODE
        ]
      };

      // Try to start with specific camera ID, fallback to facingMode
      // On iOS, always use facingMode (more reliable than deviceId)
      let cameraConfig;
      if (isIOS()) {
        // Always use facingMode on iOS - deviceId enumeration is unreliable
        cameraConfig = { facingMode: "environment" };
        console.log('[ScanScreen] iOS detected, using facingMode only');
      } else if (selectedCamera && selectedCamera.id) {
        cameraConfig = { deviceId: { exact: selectedCamera.id } };
        setCameraId(selectedCamera.id);
      } else {
        cameraConfig = { facingMode: "environment" };
      }

      console.log('[ScanScreen] Starting scanner with config:', { cameraConfig, fps: config.fps });
      // #region agent log (H1/H3/H5) before html5QrCode.start
      fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H3/H5',location:'src/screens/ScanScreen.js:startScanning:beforeStart',message:'About to call html5QrCode.start',data:{cameraConfig:{hasExactDeviceId:!!cameraConfig?.deviceId?.exact,hasFacingMode:!!cameraConfig?.facingMode},videoConstraints:config.videoConstraints,formatsToSupportCount:(config.formatsToSupport||[]).length,container:!!document.getElementById('scanner-container'),videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
      // #endregion agent log
      
      try {
        await html5QrCode.start(
          cameraConfig,
          config,
          onScanSuccess,
          onScanFailure
        );
        setScanning(true);
        console.log('[ScanScreen] Scanner started successfully');
        // #region agent log (H1/H3) html5QrCode.start success
        fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H3',location:'src/screens/ScanScreen.js:startScanning:started',message:'Scanner started successfully',data:{videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState,enabled:x.enabled}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
        // #endregion agent log
      } catch (startErr) {
        // If exact device ID fails, try with facingMode as fallback
        if (selectedCamera && selectedCamera.id && startErr.message && startErr.message.includes('device')) {
          console.log('[ScanScreen] Device ID failed, trying facingMode fallback');
          try {
            await html5QrCode.start(
              { facingMode: "environment" },
              config,
              onScanSuccess,
              onScanFailure
            );
            setScanning(true);
            console.log('[ScanScreen] Scanner started successfully with facingMode');
          } catch (fallbackErr) {
            console.error('[ScanScreen] Scanner start error (fallback also failed):', fallbackErr);
            throw fallbackErr;
          }
        } else {
          console.error('[ScanScreen] Scanner start error:', startErr);
          throw startErr;
        }
      }
    } catch (err) {
      console.error('[ScanScreen] Error starting scanner:', err);
      // #region agent log (H1/H2/H3) startScanning error
      fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H2/H3',location:'src/screens/ScanScreen.js:startScanning:error',message:'Error starting scanner',data:{name:err?.name,message:String(err?.message||err),scanning,hasRef:!!html5QrCodeRef.current,videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
      // #endregion agent log
      
      // Only show error if it's a real permission/access issue
      // Don't show errors for temporary issues that might resolve
      let errorMessage = null;
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'הגישה למצלמה נדחתה. אנא אפשר גישה למצלמה בהגדרות הדפדפן ולחץ על "נסה שוב".';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'לא נמצאה מצלמה. אנא ודא שהמכשיר שלך כולל מצלמה וגש דרך HTTPS.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'המצלמה תפוסה על ידי אפליקציה אחרת. אנא סגור אפליקציות אחרות ונסה שוב.';
      } else if (err.message && err.message.includes('No camera found')) {
        errorMessage = 'לא נמצאה מצלמה. אנא ודא שהמכשיר שלך כולל מצלמה וגש דרך HTTPS.';
      } else if (err.message && (err.message.includes('qrbox') || err.message.includes('50px'))) {
        errorMessage = 'שגיאת תצורת סורק. אנא רענן את הדף ונסה שוב.';
        console.error('[ScanScreen] QRBox configuration error:', err);
      } else {
        // For other errors, check if we're on HTTP and suggest HTTPS
        const isHttp = window.location.protocol === 'http:';
        if (isHttp && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          errorMessage = 'גישה למצלמה דורשת HTTPS. אנא גש דרך https://';
        } else {
          // Show generic error with details for debugging
          const errMsg = err.message || err.toString() || 'שגיאה לא ידועה';
          // Truncate very long error messages to prevent UI issues
          const displayMsg = errMsg.length > 100 ? errMsg.substring(0, 100) + '...' : errMsg;
          errorMessage = `שגיאה בהפעלת המצלמה: ${displayMsg}`;
          console.error('[ScanScreen] Scanner error details:', {
            name: err.name,
            message: err.message,
            stack: err.stack,
            error: err
          });
        }
      }
      
      // Clean up on error
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.clear().catch(() => {});
        } catch (clearErr) {
          console.log('[ScanScreen] Error clearing scanner on error:', clearErr);
        }
        html5QrCodeRef.current = null;
      }
      
      if (errorMessage) {
        setCameraError(errorMessage);
      }
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    try {
      // #region agent log (H1/H3) stopScanning entry
      fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H3',location:'src/screens/ScanScreen.js:stopScanning:entry',message:'stopScanning called',data:{scanning,hasRef:!!html5QrCodeRef.current,willCallStop:!!html5QrCodeRef.current&&!!scanning,videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
      // #endregion agent log
      // Clear any pending timeouts
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      
      if (html5QrCodeRef.current) {
        if (scanning) {
          await html5QrCodeRef.current.stop().catch(() => {});
        }
        await html5QrCodeRef.current.clear().catch(() => {});
        html5QrCodeRef.current = null;
        setScanning(false);
        // IMPORTANT: Do not change isScanningPaused here.
        // Callers control pause state. Resetting it here can cause duplicate-scan loops.
      }

      // #region agent log (H1/H3) stopScanning exit
      fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1/H3',location:'src/screens/ScanScreen.js:stopScanning:exit',message:'stopScanning finished',data:{scanningAfter:false,hasRefAfter:!!html5QrCodeRef.current,container:!!document.getElementById('scanner-container'),videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
      // #endregion agent log
      
      // Also clear the scanner container DOM element
      const scannerContainer = document.getElementById('scanner-container');
      if (scannerContainer) {
        // #region agent log (H6/H7) stopScanning clearing DOM
        fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H6/H7',location:'src/screens/ScanScreen.js:stopScanning:clearDom',message:'Clearing scanner-container innerHTML in stopScanning',data:{hasVideo:!!document.querySelector('#scanner-container video'),videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject},timestamp:Date.now()})}).catch(()=>{});
        // #endregion agent log
        scannerContainer.innerHTML = '';
      }
    } catch (err) {
      console.error('[ScanScreen] Error stopping scanner:', err);
      setScanning(false);
      // IMPORTANT: Do not change isScanningPaused here (see note above).
      
      // Ensure DOM is cleared even on error
      const scannerContainer = document.getElementById('scanner-container');
      if (scannerContainer) {
        scannerContainer.innerHTML = '';
      }
    }
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    const now = Date.now();
    const barcode = String(decodedText || '').trim();
    if (!barcode) return;

    // Hard guard: ignore if we've recently processed this barcode (success or not-found).
    const processed = processedBarcodesRef.current.get(barcode);
    if (processed && (now - processed.t) < BARCODE_COOLDOWN_MS) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ScanScreen] Ignoring recently processed barcode:', barcode, processed);
      }
      return;
    }

    // In-flight guard: ignore duplicate callbacks for the same barcode while processing
    if (inFlightBarcodeRef.current === barcode) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ScanScreen] Ignoring in-flight duplicate barcode callback:', barcode);
      }
      return;
    }

    // CRITICAL: Check and set flag in one atomic operation
    if (isAddingProductRef.current) {
      console.log('[ScanScreen] Already processing, ignoring callback');
      return;
    }
    
    // Prevent multiple scans
    if (isScanningPaused || loading) {
      return;
    }
    
    // CRITICAL FIX: Set flag IMMEDIATELY before ANY other operations
    // This prevents the callback from firing multiple times
    isAddingProductRef.current = true;
    inFlightBarcodeRef.current = barcode;
    
    // IMMEDIATELY stop the scanner to prevent more callbacks
    try {
      if (html5QrCodeRef.current && scanning) {
        // #region agent log (H1) onScanSuccess immediate stop (pre)
        fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1',location:'src/screens/ScanScreen.js:onScanSuccess:immediateStop:pre',message:'Attempting immediate stop in onScanSuccess',data:{scanning,hasRef:!!html5QrCodeRef.current,videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});
        // #endregion agent log
        // Stop scanner SYNCHRONOUSLY - don't await
        html5QrCodeRef.current.stop().then(()=>{fetch('http://127.0.0.1:7242/ingest/fe8bdfd5-9df3-497b-8d19-cb1733c4872b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1',location:'src/screens/ScanScreen.js:onScanSuccess:immediateStop:resolved',message:'Immediate stop promise resolved',data:{videoHasSrcObject:!!document.querySelector('#scanner-container video')?.srcObject,videoTrackStates:(()=>{try{const s=document.querySelector('#scanner-container video')?.srcObject;const t=s?.getTracks?.()||[];return t.map(x=>({kind:x.kind,readyState:x.readyState}));}catch(e){return null;}})()},timestamp:Date.now()})}).catch(()=>{});}).catch(()=>{});
        setScanning(false);
      }
    } catch (err) {
      console.error('[ScanScreen] Error stopping scanner immediately:', err);
    }
    
    // Prevent scanning the same barcode multiple times in quick succession (secondary guard)
    if (lastScannedBarcodeRef.current === barcode) {
      if (lastScanTimeRef.current && (now - lastScanTimeRef.current) < BARCODE_COOLDOWN_MS) {
        console.log('[ScanScreen] Ignoring duplicate barcode scan (within cooldown)');
        isAddingProductRef.current = false;
        inFlightBarcodeRef.current = null;
        return;
      }
    }
    
    console.log('[ScanScreen] Barcode scanned:', barcode, decodedResult);
    
    // Mark this barcode as scanned with timestamp
    lastScannedBarcodeRef.current = barcode;
    lastScanTimeRef.current = now;
    
    // Clear any existing timeout
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    
    // Pause scanning to prevent duplicate scans
    setIsScanningPaused(true);
    
    // Haptic feedback only (audio removed to prevent errors)
    try {
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
    } catch (e) {
      // Ignore vibration errors
    }
    
    // Stop scanning once we get a result
    await stopScanning();
    
    // Small delay to ensure scanner cleanup is complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Fetch product data
    try {
      await fetchProductData(barcode);
    } catch (err) {
      console.error('[ScanScreen] Error in fetchProductData:', err);
      // Reset flag on error so user can try again
      isAddingProductRef.current = false;
      // Keep paused on error to avoid immediate re-scan loops.
      setIsScanningPaused(true);
      inFlightBarcodeRef.current = null;
    }
  };

  const onScanFailure = (errorMessage) => {
    // Ignore continuous scan failures - they're normal
    // Only log if it's a real error (not the common "NotFoundException")
    if (errorMessage && !errorMessage.includes('NotFoundException')) {
      console.log('[ScanScreen] Scan failure:', errorMessage);
    }
  };

  const fetchProductData = async (barcode) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[ScanScreen] Fetching product data for barcode:', barcode);
      const response = await fetch(
        `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`
      );
      
      if (!response.ok) {
        throw new Error('Network error while fetching product data');
      }
      
      const data = await response.json();
      
      if (data.status === 1 && data.product) {
        console.log('[ScanScreen] Product found:', data.product.product_name);
        const parsedProduct = parseProductData(data.product);
        
        // Automatically add product to database and today's entries
        await autoAddProduct(parsedProduct);
      } else {
        // This is a *product* error, not a camera error.
        setCameraError(null);
        setError('המוצר לא נמצא במאגר. אנא נסה ברקוד אחר או הוסף ידנית.');
        setIsScanningPaused(true); // Keep paused to prevent re-scanning
        isAddingProductRef.current = false;
        // Mark as processed to avoid immediate re-trigger loops while the barcode is still visible
        processedBarcodesRef.current.set(barcode, { t: Date.now(), status: 'not_found' });
        inFlightBarcodeRef.current = null;
        
        // Ensure scanner is stopped to prevent re-scanning the same barcode
        if (html5QrCodeRef.current && scanning) {
          try {
            await html5QrCodeRef.current.stop().catch(() => {});
            setScanning(false);
          } catch (err) {
            console.error('[ScanScreen] Error stopping scanner on product not found:', err);
          }
        }
        
        // Clear timeout if exists
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
          scanTimeoutRef.current = null;
        }
        
        // DO NOT clear lastScannedBarcodeRef - keep it to prevent re-scanning the same barcode
        // DO NOT automatically restart scanning - require user to click "Scan Another"
        // This prevents infinite loops when the same barcode is still visible
      }
    } catch (err) {
      console.error('[ScanScreen] Error fetching product:', err);
      // This is a *network/product fetch* error, not a camera error.
      setCameraError(null);
      setError('נכשל בטעינת מידע על המוצר. אנא בדוק את החיבור לאינטרנט ונסה שוב.');
      setIsScanningPaused(true); // Keep paused to prevent re-scanning
      isAddingProductRef.current = false;
      processedBarcodesRef.current.set(barcode, { t: Date.now(), status: 'fetch_error' });
      inFlightBarcodeRef.current = null;
      
      // Ensure scanner is stopped to prevent re-scanning the same barcode
      if (html5QrCodeRef.current && scanning) {
        try {
          await html5QrCodeRef.current.stop().catch(() => {});
          setScanning(false);
        } catch (stopErr) {
          console.error('[ScanScreen] Error stopping scanner on fetch error:', stopErr);
        }
      }
      
      // Clear timeout if exists
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      
      // DO NOT clear lastScannedBarcodeRef - keep it to prevent re-scanning the same barcode
      // DO NOT automatically restart scanning - require user to click "Scan Another"
      // This prevents infinite loops when the same barcode is still visible
    } finally {
      setLoading(false);
    }
  };

  const autoAddProduct = async (product) => {
    // Note: Flag is already set in onScanSuccess, but keep this as safety check
    // This should never trigger since flag is set before fetchProductData is called
    if (!isAddingProductRef.current) {
      console.warn('[ScanScreen] Flag not set, setting it now (should not happen)');
      isAddingProductRef.current = true;
    }
    
    try {
      setLoading(true);
      
      // Default quantity: 100g (standard serving for scanned products)
      const defaultGrams = 100;
      
      // Calculate nutritional values for the default quantity
      const calories = (product.calories / 100) * defaultGrams;
      const protein = (product.protein / 100) * defaultGrams;
      const carbs = (product.carbs / 100) * defaultGrams;
      const fat = (product.fat / 100) * defaultGrams;
      
      // Check if food already exists
      const allFoods = await getAllFoods();
      const existingFood = allFoods.find(f => 
        f.name.toLowerCase().trim() === product.name.toLowerCase().trim()
      );
      
      let foodId;
      
      if (existingFood) {
        // Food already exists, use its ID
        foodId = existingFood.id;
        console.log('[ScanScreen] Food already exists, using ID:', foodId);
      } else {
        // Add food to database
        const added = await addFood(
          product.name,
          product.calories, // per 100g
          product.protein,  // per 100g
          product.carbs,    // per 100g
          product.fat,      // per 100g
          100,              // base_amount: 100g
          'gram'            // base_amount_unit: gram
        );
        
        if (!added) {
          // If addFood failed, try to find it anyway (might have been added by another process)
          const foodsAfter = await getAllFoods();
          const foundFood = foodsAfter.find(f => 
            f.name.toLowerCase().trim() === product.name.toLowerCase().trim()
          );
          
          if (foundFood) {
            foodId = foundFood.id;
          } else {
            throw new Error('Failed to add food to database');
          }
        } else {
          // Get the newly added food ID
          const foodsAfter = await getAllFoods();
          const newFood = foodsAfter.find(f => 
            f.name.toLowerCase().trim() === product.name.toLowerCase().trim()
          );
          foodId = newFood ? newFood.id : null;
        }
      }
      
      if (!foodId) {
        throw new Error('Could not get food ID');
      }
      
      // Add entry for today
      const entryAdded = await addEntry(
        foodId,
        defaultGrams,
        calories,
        protein,
        carbs,
        fat
      );
      
      if (entryAdded) {
        success(`מוצר "${product.name}" נוסף בהצלחה ליום זה! (${defaultGrams}גרם)`);
        // Mark barcode as successfully processed to prevent repeat adds while still in view
        const barcode = String(product.barcode || '').trim();
        if (barcode) {
          processedBarcodesRef.current.set(barcode, { t: Date.now(), status: 'success' });
        }
        
        // Show the added product briefly
        setLastAddedProduct({
          name: product.name,
          grams: defaultGrams,
          calories: calories.toFixed(0),
        });
        
        // Reset state immediately
        setProduct(null);
        setError(null);
        // Keep scanning paused until we intentionally restart the scanner.
        // This prevents immediate re-entry while UI is showing success.
        setIsScanningPaused(true);
        
        // IMPORTANT: DO NOT clear the barcode reference yet!
        // Keep it to prevent re-scanning the same barcode when scanner restarts
        // This prevents the infinite loop
        
        // Clear any existing timeout
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }
        
        // Restart scanning after delay to allow user to move camera away
        scanTimeoutRef.current = setTimeout(async () => {
          // Clear the last added product display
          setLastAddedProduct(null);
          
          // Ensure scanner is completely stopped before restarting
          await stopScanning();
          
          // Wait to ensure camera is released and DOM is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // IMPORTANT:
          // Do NOT clear lastScannedBarcodeRef/lastScanTimeRef here.
          // Clearing them causes the same visible barcode to be treated as "new" and added repeatedly.
          // If the user wants to scan the same item again intentionally, they can tap "Scan Another".
          
          // Reset the adding flag
          isAddingProductRef.current = false;
          // Unpause now that we're about to restart scanning
          setIsScanningPaused(false);
        inFlightBarcodeRef.current = null;
          
          // Start scanning again
          await startScanning();
        }, 3000); // 3 seconds - enough time to move camera away
      } else {
        throw new Error('Failed to add entry');
      }
    } catch (err) {
      console.error('[ScanScreen] Error auto-adding product:', err);
      showError('נכשל בהוספת המוצר. אנא נסה שוב.');
      setProduct(null);
      setIsScanningPaused(false);
      
      // Reset the adding flag on error
      isAddingProductRef.current = false;
      inFlightBarcodeRef.current = null;
      
      // Clear timeout if exists
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      
      // DO NOT automatically restart scanning - require user to click "Scan Another"
      // DO NOT clear lastScannedBarcodeRef - keep it to prevent re-scanning the same barcode
      // This prevents infinite loops when the same barcode is still visible
    } finally {
      setLoading(false);
    }
  };

  const parseProductData = (productData) => {
    return {
      name: productData.product_name || productData.product_name_en || 'Unknown Product',
      brand: productData.brands || productData.brand || 'Unknown Brand',
      barcode: productData.code || '',
      calories: productData.nutriments?.['energy-kcal_100g'] || 
                productData.nutriments?.['energy-kcal'] || 0,
      protein: productData.nutriments?.proteins_100g || 
               productData.nutriments?.proteins || 0,
      carbs: productData.nutriments?.carbohydrates_100g || 
             productData.nutriments?.carbohydrates || 0,
      fat: productData.nutriments?.fat_100g || 
           productData.nutriments?.fat || 0,
      ingredients: productData.ingredients_text || 
                   productData.ingredients_text_en || 
                   'No ingredients information available',
      imageUrl: productData.image_url || 
                productData.image_front_url || 
                productData.image_small_url || null,
      servingSize: productData.serving_size || null,
    };
  };

  const handleAddToDatabase = async () => {
    if (!product) return;
    
    setLoading(true);
    try {
      // Use new schema - add as per 100g (standard for scanned products)
      const addSuccess = await addFood(
        product.name,
        product.calories,
        product.protein,
        product.carbs,
        product.fat,
        100, // base_amount: 100g
        'gram' // base_amount_unit: gram
      );
      
      if (addSuccess) {
        success('מוצר נוסף למאגר בהצלחה!');
        // Reset to scan another
        setProduct(null);
        setError(null);
        // Mark this barcode as processed to avoid immediately re-scanning it if still in view
        if (product?.barcode) {
          processedBarcodesRef.current.set(String(product.barcode).trim(), { t: Date.now(), status: 'success' });
        }
        setTimeout(async () => {
          await stopScanning();
          await new Promise(resolve => setTimeout(resolve, 200));
          await startScanning();
        }, 500);
      } else {
        showError('נכשל בהוספת המוצר למאגר');
      }
    } catch (err) {
      console.error('[ScanScreen] Error adding product:', err);
      showError('נכשל בהוספת המוצר למאגר');
    } finally {
      setLoading(false);
    }
  };

  const handleScanAnother = async () => {
    // Clear any pending timeouts first
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    
    // Reset the adding flag
    isAddingProductRef.current = false;
    
    // Stop and clean up existing scanner instance
    await stopScanning();
    
    // Reset state
    setProduct(null);
    setError(null);
    setCameraError(null);
    setLastAddedProduct(null);
    setIsScanningPaused(false);
    setLoading(false);
    
    // IMPORTANT:
    // Do NOT clear lastScannedBarcodeRef here.
    // If the user is still pointing at the same barcode (especially one not found),
    // clearing it causes an immediate re-scan loop.
    // Scanning a *different* product works fine even with this lock kept.
    
    // Clear the scanner container DOM element completely
    const scannerContainer = document.getElementById('scanner-container');
    if (scannerContainer) {
      scannerContainer.innerHTML = '';
    }
    
    // Wait a bit for cleanup to complete and DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Start scanning again
    await startScanning();
  };

  // Switch camera (front/back)
  const switchCamera = async () => {
    if (!scanning || availableCameras.length < 2) return;
    
    try {
      await stopScanning();
      
      // Find opposite camera
      const currentIndex = availableCameras.findIndex(c => c.id === cameraId);
      const nextIndex = (currentIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextIndex];
      
      setCameraId(nextCamera.id);
      
      // Restart with new camera
      setTimeout(() => {
        startScanning();
      }, 500);
    } catch (err) {
      console.error('[ScanScreen] Error switching camera:', err);
      showError('נכשל בהחלפת מצלמה');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.userName}>{userNickname} ❤️</div>
          </div>
        </div>

        {/* Title */}
        <div style={styles.titleSection}>
          <div style={styles.title}>סרוק מוצר</div>
          <div style={styles.subtitle}>כוון את המצלמה לברקוד או ל-QR</div>
        </div>

        {/* Scanner Container */}
        {!product && !loading && !lastAddedProduct && (
          <div style={styles.scannerSection}>
            {/* Always render scanner container so it exists in DOM */}
            <div id="scanner-container" style={styles.scannerContainer}></div>
            
            {/* Show error overlay when there's an error */}
            {cameraError && !scanning && (
              <div style={styles.errorCard}>
                <div style={styles.errorIcon}>⚠️</div>
                <div style={styles.errorTitle}>נדרש גישה למצלמה</div>
                <div style={styles.errorMessage}>{cameraError}</div>
                {window.location.protocol === 'http:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && (
                  <div style={styles.httpsHint}>
                    <div style={styles.httpsHintText}>
                      💡 גישה למצלמה דורשת HTTPS
                    </div>
                    <div style={styles.httpsHintSubtext}>
                      אנא גש לאפליקציה דרך: <strong>https://{window.location.host}</strong>
                    </div>
                    <button 
                      style={styles.httpsButton}
                      onClick={() => {
                        const httpsUrl = window.location.href.replace('http://', 'https://');
                        window.location.href = httpsUrl;
                      }}
                      className="btn-primary">
                      עבור ל-HTTPS עכשיו
                    </button>
                  </div>
                )}
                <button 
                  style={styles.retryButton} 
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[ScanScreen] Retry button clicked');
                    try {
                      await startScanning();
                    } catch (err) {
                      console.error('[ScanScreen] Error in startScanning from retry button:', err);
                      setCameraError(`שגיאה: ${err.message || 'נכשל בהפעלת המצלמה'}`);
                    }
                  }}>
                  נסה שוב
                </button>
              </div>
            )}
            
            {scanning && (
              <>
                <div style={styles.scannerOverlay}>
                  <div style={styles.scannerFrame}>
                    <div style={{...styles.corner, ...styles.cornerTopLeft}}></div>
                    <div style={{...styles.corner, ...styles.cornerTopRight}}></div>
                    <div style={{...styles.corner, ...styles.cornerBottomLeft}}></div>
                    <div style={{...styles.corner, ...styles.cornerBottomRight}}></div>
                  </div>
                  <div style={styles.scannerInstructions}>
                    מיקם את הברקוד בתוך המסגרת
                  </div>
                </div>
                {availableCameras.length > 1 && (
                  <button 
                    style={styles.switchCameraButton}
                    onClick={switchCamera}
                    aria-label="Switch camera"
                    type="button">
                    <span aria-hidden="true">🔄</span> החלף מצלמה
                  </button>
                )}
              </>
            )}
            
            {!scanning && !cameraError && (
              <button 
                style={styles.startButton} 
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[ScanScreen] Start button clicked');
                  // Ensure error is cleared
                  setCameraError(null);
                  setError(null);
                  // Wait a moment for state to update
                  await new Promise(resolve => setTimeout(resolve, 50));
                  try {
                    await startScanning();
                  } catch (err) {
                    console.error('[ScanScreen] Error in startScanning from button:', err);
                    setCameraError(`שגיאה: ${err.message || 'נכשל בהפעלת המצלמה'}`);
                  }
                }}
                aria-label="Start barcode scanning"
                type="button">
                התחל סריקה
              </button>
            )}
          </div>
        )}

        {/* Loading State - only show if not showing success */}
        {loading && !lastAddedProduct && !error && (
          <div style={styles.loadingCard}>
            <div className="spinner" style={styles.loadingSpinner}></div>
            <div style={styles.loadingText}>טוען מידע על המוצר...</div>
          </div>
        )}

        {/* Success State - Product Added (only show if no errors) */}
        {lastAddedProduct && !error && !cameraError && (
          <div style={styles.successCard}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successTitle}>נוסף בהצלחה!</div>
            <div style={styles.successProductName}>{lastAddedProduct.name}</div>
            <div style={styles.successDetails}>
              {lastAddedProduct.grams}גרם • {lastAddedProduct.calories} קלוריות
            </div>
            <div style={styles.successMessage}>ממשיך לסריקה הבאה...</div>
          </div>
        )}

        {/* Error State - only show if not showing success */}
        {error && !loading && !lastAddedProduct && (
          <div style={styles.errorCard}>
            <div style={styles.errorIcon}>❌</div>
            <div style={styles.errorTitle}>שגיאה</div>
            <div style={styles.errorMessage}>{error}</div>
            <button style={styles.retryButton} onClick={handleScanAnother}>
              סרוק אחר
            </button>
          </div>
        )}

        {/* Product Display (only shown if manual add is needed) */}
        {product && !loading && !lastAddedProduct && (
          <div style={styles.productSection}>
            {/* Product Image */}
            {product.imageUrl && (
              <div style={styles.imageContainer}>
                <img 
                  src={product.imageUrl} 
                  alt={product.name}
                  style={styles.productImage}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Product Info Card */}
            <div style={styles.card} className="card-hover">
              <div style={styles.productName}>{product.name}</div>
              <div style={styles.productBrand}>{product.brand}</div>
              {product.barcode && (
                <div style={styles.productBarcode}>ברקוד: {product.barcode}</div>
              )}
            </div>

            {/* Nutrition Facts Card */}
            <div style={styles.card} className="card-hover">
              <div style={styles.cardTitle}>ערכים תזונתיים (ל-100גרם)</div>
              <div style={styles.nutritionGrid}>
                <div style={styles.nutritionItem}>
                  <div style={styles.nutritionValue}>{product.calories.toFixed(1)}</div>
                  <div style={styles.nutritionLabel}>קלוריות</div>
                </div>
                <div style={styles.nutritionItem}>
                  <div style={styles.nutritionValue}>{product.protein.toFixed(1)}g</div>
                  <div style={styles.nutritionLabel}>חלבון</div>
                </div>
                <div style={styles.nutritionItem}>
                  <div style={styles.nutritionValue}>{product.carbs.toFixed(1)}g</div>
                  <div style={styles.nutritionLabel}>פחמימות</div>
                </div>
                <div style={styles.nutritionItem}>
                  <div style={styles.nutritionValue}>{product.fat.toFixed(1)}g</div>
                  <div style={styles.nutritionLabel}>שומן</div>
                </div>
              </div>
            </div>

            {/* Ingredients Card */}
            {product.ingredients && product.ingredients !== 'No ingredients information available' && (
              <div style={styles.card} className="card-hover">
                <div style={styles.cardTitle}>מרכיבים</div>
                <div style={styles.ingredientsText}>{product.ingredients}</div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.actionButtons}>
              <button 
                style={styles.addButton} 
                onClick={handleAddToDatabase}
                disabled={loading}
                className="btn-primary"
                aria-label="Add scanned product to food database">
                הוסף למאגר
              </button>
              <button 
                style={styles.scanAnotherButton} 
                onClick={handleScanAnother}
                disabled={loading}
                className="btn-secondary"
                aria-label="Scan another product">
                סרוק אחר
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
  },
  scrollView: {
    flex: 1,
    paddingBottom: 20,
    overflowY: 'auto',
  },
  header: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 20,
    paddingBottom: 10,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  titleSection: {
    paddingLeft: 20,
    paddingRight: 20,
    marginBottom: 32,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 8,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: 14,
    color: colors.textTertiary,
  },
  scannerSection: {
    paddingLeft: 20,
    paddingRight: 20,
    marginBottom: 20,
    position: 'relative',
    minHeight: '300px',
  },
  scannerContainer: {
    width: '100%',
    minHeight: '300px',
    minWidth: '250px', // Ensure minimum width for qrbox calculation
    backgroundColor: colors.surface,
    borderRadius: 15,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`,
    position: 'relative',
  },
  scannerOverlay: {
    position: 'absolute',
    top: 0,
    left: 20,
    right: 20,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none',
    zIndex: 10,
  },
  scannerFrame: {
    width: '70%',
    maxWidth: 300,
    aspectRatio: 1,
    position: 'relative',
    marginBottom: 20,
  },
  corner: {
    position: 'absolute',
    width: 30,
    height: 30,
    borderColor: colors.primary,
    borderWidth: '3px',
    borderStyle: 'solid',
  },
  cornerTopLeft: {
    top: 0,
    left: 0,
    borderRight: 'none',
    borderBottom: 'none',
    borderTopLeftRadius: 8,
  },
  cornerTopRight: {
    top: 0,
    right: 0,
    borderLeft: 'none',
    borderBottom: 'none',
    borderTopRightRadius: 8,
  },
  cornerBottomLeft: {
    bottom: 0,
    left: 0,
    borderRight: 'none',
    borderTop: 'none',
    borderBottomLeftRadius: 8,
  },
  cornerBottomRight: {
    bottom: 0,
    right: 0,
    borderLeft: 'none',
    borderTop: 'none',
    borderBottomRightRadius: 8,
  },
  scannerInstructions: {
    backgroundColor: colors.overlay,
    color: colors.textOnPrimary,
    padding: '12px 24px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${colors.shadowPrimaryLight}`,
  },
  switchCameraButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: colors.surface,
    color: colors.primary,
    padding: '12px 20px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
    zIndex: 20,
    pointerEvents: 'auto',
    backdropFilter: 'blur(10px)',
    transition: 'all 200ms ease',
  },
  startButton: {
    width: '100%',
    backgroundColor: colors.primary,
    color: colors.textOnPrimary,
    padding: 18,
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    marginTop: 10,
    position: 'relative',
    zIndex: 100,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    userSelect: 'none',
  },
  loadingCard: {
    backgroundColor: colors.surface,
    marginLeft: 20,
    marginRight: 20,
    marginBottom: 20,
    padding: 40,
    borderRadius: 15,
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingSpinner: {
    marginBottom: 20,
  },
  loadingText: {
    color: colors.textPrimary,
    fontSize: 16,
  },
  errorCard: {
    backgroundColor: colors.surface,
    marginLeft: 20,
    marginRight: 20,
    marginBottom: 20,
    padding: 30,
    borderRadius: 15,
    border: `1px solid ${colors.error}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    position: 'absolute',
    top: 0,
    left: 20,
    right: 20,
    zIndex: 100,
    marginTop: 0,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 15,
  },
  errorTitle: {
    color: colors.error,
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  errorMessage: {
    color: colors.textPrimary,
    fontSize: 14,
    marginBottom: 20,
    lineHeight: 1.5,
  },
  retryButton: {
    backgroundColor: colors.primary,
    color: colors.textOnPrimary,
    padding: '12px 24px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    marginTop: 12,
  },
  httpsHint: {
    backgroundColor: colors.surfaceAlt,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    border: `1px solid ${colors.primary}`,
    width: '100%',
  },
  httpsHintText: {
    color: colors.primary,
    fontSize: 16,
    marginBottom: 8,
    textAlign: 'center',
    fontWeight: '600',
  },
  httpsHintSubtext: {
    color: colors.textTertiary,
    fontSize: 12,
    marginBottom: 16,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  httpsButton: {
    width: '100%',
    backgroundColor: colors.primary,
    color: colors.textOnPrimary,
    padding: '12px 24px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
  },
  productSection: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  imageContainer: {
    width: '100%',
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  productImage: {
    maxWidth: '100%',
    maxHeight: '300px',
    borderRadius: 15,
    border: `1px solid ${colors.border}`,
  },
  card: {
    backgroundColor: colors.surface,
    marginBottom: 16,
    padding: 24,
    borderRadius: 16,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'all 200ms ease',
  },
  productName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 5,
  },
  productBrand: {
    fontSize: 16,
    color: colors.textTertiary,
    marginBottom: 10,
  },
  productBarcode: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 15,
  },
  nutritionGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  nutritionItem: {
    width: '48%',
    backgroundColor: colors.bg,
    padding: 15,
    borderRadius: 10,
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: `1px solid ${colors.border}`,
  },
  nutritionValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
  },
  nutritionLabel: {
    fontSize: 12,
    color: colors.textTertiary,
    marginTop: 6,
  },
  ingredientsText: {
    fontSize: 14,
    color: colors.textPrimary,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 16,
  },
  addButton: {
    width: '100%',
    height: 56,
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    color: colors.textOnPrimary,
    padding: '16px 24px',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    boxShadow: `0 4px 12px ${colors.shadowPrimary}`,
    transition: 'all 200ms ease',
    letterSpacing: 0.3,
  },
  scanAnotherButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.surface,
    color: colors.primary,
    padding: '12px 20px',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'all 200ms ease',
    letterSpacing: 0.3,
  },
  successCard: {
    backgroundColor: colors.surface,
    marginLeft: 20,
    marginRight: 20,
    marginBottom: 20,
    padding: 30,
    borderRadius: 15,
    border: `1px solid ${colors.primary}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    boxShadow: `0 4px 12px ${colors.shadowPrimaryLight}`,
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 15,
  },
  successTitle: {
    color: colors.primary,
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  successProductName: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  successDetails: {
    color: colors.textTertiary,
    fontSize: 14,
    marginBottom: 15,
  },
  successMessage: {
    color: colors.textSecondary,
    fontSize: 12,
    fontStyle: 'italic',
  },
};

export default ScanScreen;

